// The send-later choice of a card's armed send (U12), kept beside the card controller so the
// approve strip can set it and the card action can read it without threading new state through
// the controller: null sends after the undo window as always, "" means a time is being picked
// (Send waits for it), an ISO time schedules the send. "Change" on a scheduled card leaves the
// time it had, so the strip opens on it the next time the send is armed.
import { useSyncExternalStore } from "react";

export type SendWhen = string | null;

const choices = new Map<string, SendWhen>();
const reopen = new Map<string, string>();
const listeners = new Set<() => void>();

const emit = () => {
  for (const l of listeners) l();
};

export const sendLaterStore = {
  get(cardId: string): SendWhen {
    return choices.get(cardId) ?? null;
  },
  set(cardId: string, when: SendWhen): void {
    if ((choices.get(cardId) ?? null) === when) return;
    if (when === null) choices.delete(cardId);
    else choices.set(cardId, when);
    emit();
  },
  clear(cardId: string): void {
    if (!choices.delete(cardId)) return;
    emit();
  },
  /** "Change": the next armed send of this card starts on `at`, with the picker open. */
  requestChange(cardId: string, at: string): void {
    reopen.set(cardId, at);
  },
  /** The time a "Change" left for this card, without using it up. */
  peekChange(cardId: string): string | null {
    return reopen.get(cardId) ?? null;
  },
  /** The time a "Change" left for this card, once. */
  takeChange(cardId: string): string | null {
    const at = reopen.get(cardId) ?? null;
    reopen.delete(cardId);
    return at;
  },
  subscribe(listener: () => void): () => void {
    listeners.add(listener);
    return () => listeners.delete(listener);
  },
};

export function useCardSendAt(cardId: string): SendWhen {
  return useSyncExternalStore(
    sendLaterStore.subscribe,
    () => sendLaterStore.get(cardId),
    () => null,
  );
}
